/**
 * VersonaDebugModule — the ADMIN-ONLY in-world versona inspector.
 *
 * Commons admins Alt+Click a versona (screen-projected crowd positions — the
 * crowd renderer is instanced, so there are no per-NPC meshes to raycast) to
 * open a live panel showing EVERYTHING the sim knows: identity/family/faction,
 * needs/personality/wallet, typed emotion episodes with causes, PAD mood,
 * grudges, memories, relationships/favors, committed intent, the live goal,
 * encounters, and recent interactions — refreshed every second.
 *
 * Every EDIT goes through the admin-gated debug reducers (whitelisted +
 * clamped server-side): stats, job, injected emotions, grudges, pair
 * familiarity, goals, clear-affect, staged births. The client only renders
 * and calls — the server stays the authority even for debugging.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { capsIn } from '../lib/world_caps.js';
import type { EnvironmentModule } from './EnvironmentModule.js';

const PICK_PX = 48;
const PICK_RANGE_M = 120;

const OCC_INJECTABLE = [
  'joy', 'distress', 'hope', 'fear', 'pride', 'shame', 'admiration', 'reproach',
  'gratification', 'remorse', 'gratitude', 'anger', 'happy_for', 'resentment',
  'gloating', 'pity', 'like', 'dislike',
];
const TARGETED = new Set(['admiration', 'reproach', 'gratitude', 'anger', 'happy_for', 'resentment', 'gloating', 'pity']);
const JOBS = ['farmer', 'miner', 'smith', 'merchant', 'guard', 'bard', 'scout'];
const GOAL_STATES = ['working', 'resting', 'patrolling', 'idle'];

export interface DebugReducers {
  debugSetAgentStat(a: { versonaId: bigint; field: string; value: bigint }): Promise<void>;
  debugSetAgentJob(a: { versonaId: bigint; job: string }): Promise<void>;
  debugElicitEmotion(a: { versonaId: bigint; etype: string; intensity: number; targetId: bigint; causeText: string }): Promise<void>;
  debugSetGrudge(a: { versonaId: bigint; targetId: bigint; intensity: number; causeText: string }): Promise<void>;
  debugSetPair(a: { aId: bigint; bId: bigint; familiarity: number }): Promise<void>;
  debugClearAffect(a: { versonaId: bigint }): Promise<void>;
  setVersonaGoal(a: { versonaId: bigint; x: number; z: number; state: string; ttlSeconds: number }): Promise<void>;
  debugForceBirth(a: { settlementId: bigint }): Promise<void>;
  versonaSay(a: { versonaId: bigint; text: string }): Promise<void>;
  debugSetMemory(a: { versonaId: bigint; memoryId: bigint; kind: string; factKey: string; text: string; importance: number }): Promise<void>;
  debugForgetMemory(a: { versonaId: bigint; memoryId: bigint }): Promise<void>;
}

export class VersonaDebugModule implements IModule {
  readonly name = 'VersonaDebug';

  private scene!: THREE.Scene;
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private current: bigint | null = null;
  private timer: number | null = null;
  private subscribed = false;

  constructor(private readonly env: EnvironmentModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    window.addEventListener('pointerdown', (e) => {
      if (!e.altKey || e.button !== 0) return;
      // Only picks that land on the 3D canvas count — UI windows keep their clicks.
      if ((e.target as HTMLElement | null)?.tagName !== 'CANVAS') return;
      if (!this.isAdminHere()) return;
      const id = this.pick(e.clientX, e.clientY);
      if (id !== null) { e.preventDefault(); this.open(id); }
    }, true);
  }

  private isAdminHere(): boolean {
    return atlas.currentWorldId !== null
      && atlas.currentWorldId === atlas.commonsWorldId
      && capsIn(atlas.currentWorldId).admin;
  }

  private reducers(): DebugReducers | null {
    return (atlas.conn?.reducers as unknown as DebugReducers | undefined) ?? null;
  }

  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  /** Admin-only tables the rest of the client never needs — subscribed lazily. */
  private subHandles: Array<{ unsubscribe(): void }> = [];
  private ensureSubs(): void {
    if (this.subscribed) return;
    this.subscribed = true;
    try {
      const h = atlas.conn?.subscriptionBuilder().subscribe([
        'SELECT * FROM npc_mood', 'SELECT * FROM versona_grudge', 'SELECT * FROM versona_intent',
        'SELECT * FROM versona_favor', 'SELECT * FROM versona_goal', 'SELECT * FROM versona_lineage',
        'SELECT * FROM versona_encounter', 'SELECT * FROM versona_pair', 'SELECT * FROM versona_deliberation',
        // npc_event left the base subs (9k-row initial sync) — the inspector's
        // event feed subscribes it itself.
        'SELECT * FROM npc_event', 'SELECT * FROM settlement_civ',
      ]) as { unsubscribe(): void } | undefined;
      if (h) this.subHandles.push(h);
    } catch { /* pre-publish server */ }
  }

  /** (bug sweep) release the ~10k-row admin feed when the inspector closes. */
  private releaseSubs(): void {
    for (const h of this.subHandles) { try { h.unsubscribe(); } catch { /* gone */ } }
    this.subHandles = [];
    this.subscribed = false;
  }

  // ─── Picking (screen-projected crowd positions) ──────────────────────────────

  private pick(px: number, py: number): bigint | null {
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam) return null;
    const w = window.innerWidth, h = window.innerHeight;
    let best: string | null = null;
    let bestD = PICK_PX;
    const v = new THREE.Vector3();
    for (const p of this.env.versonaPositions()) {
      const dx = p.x - atlas.localX, dz = p.z - atlas.localZ;
      if (dx * dx + dz * dz > PICK_RANGE_M * PICK_RANGE_M) continue;
      // Render position = world + scene offset (FP64 origin rebase).
      v.set(p.x + this.scene.position.x, p.y + this.scene.position.y + 0.6, p.z + this.scene.position.z).project(cam);
      if (v.z > 1) continue; // behind the camera
      const sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
      const d = Math.hypot(sx - px, sy - py);
      if (d < bestD) { bestD = d; best = p.id; }
    }
    return best !== null ? BigInt(best) : null;
  }

  private nearby(): bigint[] {
    return this.env.versonaPositions()
      .map((p) => ({ id: p.id, d: Math.hypot(p.x - atlas.localX, p.z - atlas.localZ) }))
      .sort((a, b) => a.d - b.d)
      .map((p) => BigInt(p.id));
  }

  // ─── Panel ───────────────────────────────────────────────────────────────────

  private open(id: bigint): void {
    this.ensureSubs();
    this.current = id;
    if (!this.win) {
      this.win = makeWindow({
        id: 'versona-debug', title: 'Versona Inspector (admin)', icon: 'logic',
        width: 620, height: 760, minWidth: 480, minHeight: 400, x: 24, y: 48,
      });
      const p = document.createElement('div');
      p.style.cssText = 'display:flex;flex-direction:column;height:100%;color:var(--ax-text);font:12px/1.5 var(--ax-font);box-sizing:border-box';
      const bar = document.createElement('div');
      bar.style.cssText = 'display:flex;gap:6px;align-items:center;padding:6px 8px;border-bottom:1px solid var(--ax-line);flex-wrap:wrap';
      bar.appendChild(this.btn('◀', 'previous nearby versona', () => this.cycle(-1)));
      bar.appendChild(this.btn('Nearest', 'inspect the nearest versona', () => { const n = this.nearby()[0]; if (n !== undefined) this.open(n); }));
      bar.appendChild(this.btn('▶', 'next nearby versona', () => this.cycle(1)));
      this.note = document.createElement('div');
      this.note.style.cssText = 'flex:1;text-align:right;opacity:.85';
      bar.appendChild(this.note);
      p.appendChild(bar);
      this.body = document.createElement('div');
      this.body.style.cssText = 'flex:1;overflow:auto;padding:8px 10px';
      p.appendChild(this.body);
      this.win.body.appendChild(p);
    }
    this.win.setOpen(true);
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (this.win?.isOpen()) this.render();
        else if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
      }, 1000);
    }
    this.render();
  }

  private cycle(dir: number): void {
    const ids = this.nearby();
    if (ids.length === 0 || this.current === null) return;
    const i = ids.findIndex((x) => x === this.current);
    const next = ids[(i + dir + ids.length) % ids.length];
    if (next !== undefined) this.open(next);
  }

  private setNote(text: string, err = false): void {
    if (this.note) { this.note.textContent = text; this.note.style.color = err ? 'var(--ax-bad)' : 'var(--ax-good)'; }
  }

  private call(p: Promise<void> | undefined, done: string): void {
    if (!p) return;
    void p.then(() => { this.setNote(done); this.render(); }).catch((e: Error) => this.setNote(e.message, true));
  }

  // ─── Rendering helpers ───────────────────────────────────────────────────────

  private btn(label: string, tip: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.title = tip;
    b.style.cssText = 'padding:3px 8px;background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);font:11px var(--ax-font);cursor:pointer';
    b.onclick = onClick;
    return b;
  }

  private head(root: HTMLElement, text: string): void {
    const d = document.createElement('div');
    d.style.cssText = 'margin:10px 0 4px;font-weight:700;color:var(--ax-hi);border-bottom:1px solid var(--ax-line);padding-bottom:2px';
    d.textContent = text;
    root.appendChild(d);
  }

  private line(root: HTMLElement, text: string, dim = false): void {
    const d = document.createElement('div');
    d.style.cssText = `white-space:pre-wrap;${dim ? 'opacity:.7;' : ''}font-family:var(--ax-mono);font-size:11px`;
    d.textContent = text;
    root.appendChild(d);
  }

  /** An editable numeric stat row: label, value, input + Set. */
  private statRow(root: HTMLElement, label: string, value: string, field: string, id: bigint): void {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:6px;align-items:center;margin:1px 0';
    const l = document.createElement('span');
    l.style.cssText = 'width:110px;opacity:.8';
    l.textContent = label;
    const v = document.createElement('span');
    v.style.cssText = 'width:64px;font-family:var(--ax-mono)';
    v.textContent = value;
    const input = document.createElement('input');
    input.type = 'number';
    input.style.cssText = 'width:70px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
    const set = this.btn('set', `set ${field}`, () => {
      const n = Math.round(Number(input.value));
      if (!Number.isFinite(n)) return;
      this.call(this.reducers()?.debugSetAgentStat({ versonaId: id, field, value: BigInt(n) }), `${field} = ${n}`);
    });
    row.appendChild(l); row.appendChild(v); row.appendChild(input); row.appendChild(set);
    root.appendChild(row);
  }

  // ─── The full dump ───────────────────────────────────────────────────────────

  private render(): void {
    if (!this.body || this.current === null) return;
    // Never clobber a field being typed into — the panel re-renders every second.
    const ae = document.activeElement;
    if (ae && this.body.contains(ae) && (ae.tagName === 'INPUT' || ae.tagName === 'SELECT' || ae.tagName === 'TEXTAREA')) return;
    this.renderInto(this.body, this.current);
  }

  /**
   * Render the full inspector for `id` into `b` (the Alt+Click window AND the
   * Versona Universe window's right pane share this — one dossier, two doors).
   */
  renderInto(b: HTMLElement, id: bigint, opts?: { subscribe?: boolean; rows?: (table: string) => unknown[]; reducers?: DebugReducers }): void {
    // The Versona Universe feeds this versona's rows from an SQL poll (`rows`) — on a loaded tab every new
    // subscription on the game connection costs seconds, and the inspector's own global set stalled the rest.
    if (opts?.subscribe !== false) this.ensureSubs();
    const T = <X,>(name: string): X[] => (opts?.rows ? (opts.rows(name) as X[]) : this.tbl<X>(name)); // (a blanket rename once made this recurse)
    b.textContent = '';
    const r = opts?.reducers ?? this.reducers(); // the Universe routes edits over HTTP — the game connection's sends can lag minutes

    interface V { versonaId: bigint; archetype: string; state: string }
    interface A { versonaId: bigint; settlementId: bigint; job: string; boldness: number; greed: number; diligence: number; sociability: number; hunger: number; needRest: number; needSocial: number; mood: number; wallet: bigint; carry: number; carryGood: string; homeX: number; homeZ: number }
    const v = T<V>('versona').find((x) => x.versonaId === id);
    const a = T<A>('npcAgent').find((x) => x.versonaId === id);
    if (!v || !a) { this.line(b, 'No sim rows for this versona (yet).'); return; }

    // ── identity + family + faction ──
    const lin = T<{ childId: bigint; parentA: bigint; parentB: bigint; generation: number }>('versonaLineage');
    const mine = lin.find((l) => l.childId === id);
    const kids = lin.filter((l) => l.parentA === id || l.parentB === id);
    const nameOf = (x: bigint): string => T<V>('versona').find((q) => q.versonaId === x)?.archetype ?? `#${x}`;
    const faction = a.job === 'merchant' || a.wallet >= 60n ? 'commerce' : a.job === 'guard' || a.job === 'scout' || a.sociability >= 75 ? 'civic' : 'labor';
    // Civilization identity (settlement_civ table when replicated, else '?').
    const civRow = T<{ settlementId: bigint; civ: string }>('settlementCiv').find((c) => c.settlementId === a.settlementId);
    const civName = civRow?.civ === 'a' ? 'Aurelian Concord' : civRow?.civ === 'b' ? 'Borean Compact' : null;
    this.head(b, `#${id} — ${v.archetype} · ${a.job} · ${faction}${civName ? ` · ${civName}` : ''} · gen ${mine?.generation ?? 0} · settlement ${a.settlementId} · fsm ${v.state}`);
    if (mine) this.line(b, `parents: ${nameOf(mine.parentA)} (#${mine.parentA}) + ${nameOf(mine.parentB)} (#${mine.parentB})`);
    if (kids.length) this.line(b, `children: ${kids.map((k) => `${nameOf(k.childId)} (#${k.childId})`).join(', ')}`);

    // ── sim stats (editable) ──
    this.head(b, 'Sim stats (edit + set)');
    this.statRow(b, 'hunger', String(a.hunger), 'hunger', id);
    this.statRow(b, 'needRest', String(a.needRest), 'needRest', id);
    this.statRow(b, 'needSocial', String(a.needSocial), 'needSocial', id);
    this.statRow(b, 'mood', String(a.mood), 'mood', id);
    this.statRow(b, 'wallet ⓒ', String(a.wallet), 'wallet', id);
    this.statRow(b, 'carry', `${a.carry}${a.carryGood ? ` ${a.carryGood}` : ''}`, 'carry', id);
    this.statRow(b, 'boldness', String(a.boldness), 'boldness', id);
    this.statRow(b, 'greed', String(a.greed), 'greed', id);
    this.statRow(b, 'diligence', String(a.diligence), 'diligence', id);
    this.statRow(b, 'sociability', String(a.sociability), 'sociability', id);
    {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:6px;align-items:center;margin:2px 0';
      const l = document.createElement('span');
      l.style.cssText = 'width:110px;opacity:.8';
      l.textContent = 'job';
      const sel = document.createElement('select');
      sel.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      for (const j of JOBS) { const o = document.createElement('option'); o.value = j; o.textContent = j; sel.appendChild(o); }
      sel.value = a.job;
      row.appendChild(l); row.appendChild(sel);
      row.appendChild(this.btn('set', 'reassign job', () => this.call(r?.debugSetAgentJob({ versonaId: id, job: sel.value }), `job = ${sel.value}`)));
      b.appendChild(row);
    }

    // ── affect: mind summary, PAD mood, episodes ──
    interface Mind { versonaId: bigint; joy: number; anger: number; fear: number; hungerStreak: number }
    interface Mood { versonaId: bigint; pleasureX10: number; arousalX10: number; dominanceX10: number; basePleasureX10: number; baseArousalX10: number; baseDominanceX10: number }
    interface Emo { emotionId: bigint; versonaId: bigint; etype: string; targetId: bigint; causeText: string; intensity: number; halfLifeS: number; attendantOf: bigint; elicitedAt: { microsSinceUnixEpoch: bigint } }
    const mind = T<Mind>('npcMind').find((x) => x.versonaId === id);
    const mood = T<Mood>('npcMood').find((x) => x.versonaId === id);
    this.head(b, 'Affect');
    if (mind) this.line(b, `summary  joy ${mind.joy} · anger ${mind.anger} · fear ${mind.fear} · hungerStreak ${mind.hungerStreak}   (editable: joy/anger/fear fields above via stat names)`);
    if (mood) this.line(b, `PAD mood P ${(mood.pleasureX10 / 10).toFixed(1)} · A ${(mood.arousalX10 / 10).toFixed(1)} · D ${(mood.dominanceX10 / 10).toFixed(1)}   baseline P ${(mood.basePleasureX10 / 10).toFixed(1)} A ${(mood.baseArousalX10 / 10).toFixed(1)} D ${(mood.baseDominanceX10 / 10).toFixed(1)}`);
    const nowMs = Date.now();
    const eps = T<Emo>('versonaEmotion').filter((x) => x.versonaId === id);
    for (const ep of eps.sort((x, y) => y.intensity - x.intensity)) {
      const age = Math.max(0, nowMs / 1000 - Number(ep.elicitedAt.microsSinceUnixEpoch / 1_000_000n));
      const eff = ep.intensity * Math.pow(0.5, age / Math.max(1, ep.halfLifeS));
      this.line(b, `· ${ep.etype} ${eff.toFixed(0)}/${ep.intensity}${ep.targetId !== 0n ? ` @#${ep.targetId}` : ''}${ep.attendantOf !== 0n ? ' (resolution)' : ''} — ${ep.causeText}`);
    }
    if (eps.length === 0) this.line(b, '(no active episodes)', true);
    {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:center;flex-wrap:wrap;margin:3px 0';
      const sel = document.createElement('select');
      sel.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      for (const t of OCC_INJECTABLE) { const o = document.createElement('option'); o.value = t; o.textContent = t; sel.appendChild(o); }
      const inten = document.createElement('input');
      inten.type = 'number'; inten.value = '70';
      inten.style.cssText = 'width:52px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const target = document.createElement('input');
      target.placeholder = 'target id';
      target.style.cssText = 'width:80px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const cause = document.createElement('input');
      cause.placeholder = 'because…';
      cause.style.cssText = 'flex:1;min-width:120px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      row.appendChild(sel); row.appendChild(inten); row.appendChild(target); row.appendChild(cause);
      row.appendChild(this.btn('inject', 'elicit this emotion', () => {
        const tgt = target.value.trim() ? BigInt(target.value.trim()) : 0n;
        if (TARGETED.has(sel.value) && tgt === 0n) { this.setNote(`${sel.value} needs a target id`, true); return; }
        this.call(r?.debugElicitEmotion({ versonaId: id, etype: sel.value, intensity: Math.max(1, Math.min(100, Number(inten.value) || 70)), targetId: tgt, causeText: cause.value }), `felt ${sel.value}`);
      }));
      row.appendChild(this.btn('clear affect', 'wipe episodes + reset mind/mood', () => this.call(r?.debugClearAffect({ versonaId: id }), 'affect cleared')));
      b.appendChild(row);
    }

    // ── grudges ──
    interface Grudge { grudgeId: bigint; versonaId: bigint; targetId: bigint; causeText: string; intensity: number; offenses: number }
    this.head(b, 'Grudges');
    const grudges = T<Grudge>('versonaGrudge').filter((g) => g.versonaId === id);
    for (const g of grudges) this.line(b, `· vs ${nameOf(g.targetId)} (#${g.targetId}) — ${g.intensity} (${g.offenses} offense${g.offenses === 1 ? '' : 's'}) — ${g.causeText}`);
    if (grudges.length === 0) this.line(b, '(none)', true);
    {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:center;flex-wrap:wrap;margin:3px 0';
      const target = document.createElement('input');
      target.placeholder = 'target id';
      target.style.cssText = 'width:80px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const inten = document.createElement('input');
      inten.type = 'number'; inten.value = '75'; inten.title = '0 clears';
      inten.style.cssText = 'width:52px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const cause = document.createElement('input');
      cause.placeholder = 'over what?';
      cause.style.cssText = 'flex:1;min-width:100px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      row.appendChild(target); row.appendChild(inten); row.appendChild(cause);
      row.appendChild(this.btn('set grudge', '0 intensity clears it', () => {
        const tgt = target.value.trim() ? BigInt(target.value.trim()) : 0n;
        this.call(r?.debugSetGrudge({ versonaId: id, targetId: tgt, intensity: Math.max(0, Math.min(100, Number(inten.value) || 0)), causeText: cause.value }), 'grudge set');
      }));
      b.appendChild(row);
    }

    // ── memories (editable: every row edits in place, forgets, plus add + forget all) ──
    interface Mem { memoryId: bigint; versonaId: bigint; kind: string; factKey: string; text: string; importance: number }
    this.head(b, 'Memories (importance-ranked · edit / forget / add)');
    const mems = T<Mem>('versonaMemory').filter((m) => m.versonaId === id).sort((x, y) => y.importance - x.importance);
    const field = (w: string, ph: string, v = ''): HTMLInputElement => {
      const i = document.createElement('input'); i.placeholder = ph; i.value = v;
      i.style.cssText = `width:${w};background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px`;
      return i;
    };
    const kindSel = (v: string): HTMLSelectElement => {
      const sel = document.createElement('select');
      sel.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      for (const k of ['witness', 'rumor', 'reflection']) { const o = document.createElement('option'); o.value = k; o.textContent = k; sel.appendChild(o); }
      sel.value = v; return sel;
    };
    for (const m of mems) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:center;margin:2px 0';
      row.dataset.memoryId = String(m.memoryId);
      const imp = field('42px', 'imp', String(m.importance)); imp.type = 'number';
      const kind = kindSel(m.kind);
      const text = field('auto', 'text', m.text); text.style.flex = '1';
      row.append(imp, kind, text);
      row.appendChild(this.btn('save', 'edit this memory in place', () => this.call(r?.debugSetMemory({ versonaId: id, memoryId: m.memoryId, kind: kind.value, factKey: m.factKey, text: text.value, importance: Math.max(1, Math.min(10, Number(imp.value) || m.importance)) }), `memory #${m.memoryId} saved`)));
      row.appendChild(this.btn('forget', 'delete this memory', () => this.call(r?.debugForgetMemory({ versonaId: id, memoryId: m.memoryId }), `memory #${m.memoryId} forgotten`)));
      b.appendChild(row);
    }
    if (mems.length === 0) this.line(b, '(none)', true);
    {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:center;margin:3px 0';
      const imp = field('42px', 'imp', '6'); imp.type = 'number';
      const kind = kindSel('witness');
      const text = field('auto', 'a new memory…'); text.style.flex = '1';
      row.append(imp, kind, text);
      row.appendChild(this.btn('add', 'implant a memory', () => {
        if (!text.value.trim()) { this.setNote('memory text is empty', true); return; }
        this.call(r?.debugSetMemory({ versonaId: id, memoryId: 0n, kind: kind.value, factKey: '', text: text.value, importance: Math.max(1, Math.min(10, Number(imp.value) || 6)) }), 'memory added');
      }));
      row.appendChild(this.btn('forget all', 'wipe every memory', () => this.call(r?.debugForgetMemory({ versonaId: id, memoryId: 0n }), 'all memories forgotten')));
      b.appendChild(row);
    }

    // ── relationships + favors ──
    interface Pair { aId: bigint; bId: bigint; familiarity: number }
    interface Favor { aId: bigint; bId: bigint; balance: number }
    this.head(b, 'Relationships');
    const pairs = T<Pair>('versonaPair').filter((p) => p.aId === id || p.bId === id).sort((x, y) => y.familiarity - x.familiarity);
    for (const p of pairs.slice(0, 6)) {
      const other = p.aId === id ? p.bId : p.aId;
      this.line(b, `· ${nameOf(other)} (#${other}) — familiarity ${p.familiarity}`);
    }
    if (pairs.length === 0) this.line(b, '(no pairs yet)', true);
    for (const f of T<Favor>('versonaFavor').filter((f) => f.aId === id || f.bId === id)) {
      const other = f.aId === id ? f.bId : f.aId;
      const owed = f.aId === id ? f.balance : -f.balance;
      this.line(b, `· favor: ${owed > 0 ? `${nameOf(other)} owes me ${owed}ⓒ` : `I owe ${nameOf(other)} ${-owed}ⓒ`}`);
    }
    {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:center;margin:3px 0';
      const other = document.createElement('input');
      other.placeholder = 'other id';
      other.style.cssText = 'width:80px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const fam = document.createElement('input');
      fam.type = 'number'; fam.value = '70';
      fam.style.cssText = 'width:52px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      row.appendChild(other); row.appendChild(fam);
      row.appendChild(this.btn('set familiarity', 'drive births/encounters/gossip', () => {
        const o = other.value.trim() ? BigInt(other.value.trim()) : 0n;
        if (o === 0n) return;
        this.call(r?.debugSetPair({ aId: id, bId: o, familiarity: Math.max(0, Math.min(100, Number(fam.value) || 0)) }), 'familiarity set');
      }));
      b.appendChild(row);
    }

    // ── intent + goal + encounter ──
    interface Intent { versonaId: bigint; activity: string; commitment: number }
    interface Goal { versonaId: bigint; x: number; z: number; state: string }
    interface Enc { aId: bigint; bId: bigint; kind: string; state: string; tier: string }
    this.head(b, 'Intent · goal · encounter');
    const intent = T<Intent>('versonaIntent').find((x) => x.versonaId === id);
    if (intent) this.line(b, `intent: ${intent.activity} (commitment ${['open', 'single', 'blind'][intent.commitment] ?? intent.commitment})`);
    const goal = T<Goal>('versonaGoal').find((x) => x.versonaId === id);
    if (goal) this.line(b, `goal: → (${goal.x.toFixed(1)}, ${goal.z.toFixed(1)}) ${goal.state}`);
    const enc = T<Enc>('versonaEncounter').find((x) => x.aId === id || x.bId === id);
    if (enc) this.line(b, `encounter: ${enc.kind} ${enc.state}${enc.tier ? ` (${enc.tier})` : ''} with #${enc.aId === id ? enc.bId : enc.aId}`);
    {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:center;flex-wrap:wrap;margin:3px 0';
      const gx = document.createElement('input');
      gx.placeholder = 'x'; gx.style.cssText = 'width:70px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const gz = document.createElement('input');
      gz.placeholder = 'z'; gz.style.cssText = 'width:70px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      const gs = document.createElement('select');
      gs.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 4px';
      for (const s of GOAL_STATES) { const o = document.createElement('option'); o.value = s; o.textContent = s; gs.appendChild(o); }
      row.appendChild(gx); row.appendChild(gz); row.appendChild(gs);
      row.appendChild(this.btn('send', 'set a 30s goal', () => {
        const x = Number(gx.value), z = Number(gz.value);
        if (!Number.isFinite(x) || !Number.isFinite(z)) return;
        this.call(r?.setVersonaGoal({ versonaId: id, x, z, state: gs.value, ttlSeconds: 30 }), 'goal sent');
      }));
      row.appendChild(this.btn('come here', 'goal = my position', () =>
        this.call(r?.setVersonaGoal({ versonaId: id, x: atlas.localX, z: atlas.localZ, state: 'working', ttlSeconds: 30 }), 'summoned')));
      b.appendChild(row);
    }

    // ── recent interactions ──
    interface Chat { versonaId: bigint; speaker: string; speakerName: string; text: string; chatId: bigint }
    interface Ev { eventId: bigint; versonaId: bigint; settlementId: bigint; kind: string; detail: string }
    this.head(b, 'Recent interactions');
    const chats = T<Chat>('versonaChat').filter((c) => c.versonaId === id).sort((x, y) => (x.chatId > y.chatId ? -1 : 1)).slice(0, 5);
    for (const c of chats) this.line(b, `chat ${c.speaker === 'player' ? c.speakerName : 'npc'}: ${c.text.slice(0, 90)}`, true);
    const evs = T<Ev>('npcEvent').filter((e) => e.versonaId === id || e.settlementId === a.settlementId).sort((x, y) => (x.eventId > y.eventId ? -1 : 1));
    for (const e of evs.slice(0, 8)) this.line(b, `${e.versonaId === id ? '★' : '·'} [${e.kind}] ${e.detail}`, e.versonaId !== id);

    // ── quick scenarios ──
    this.head(b, 'Quick scenarios');
    const quick = document.createElement('div');
    quick.style.cssText = 'display:flex;gap:5px;flex-wrap:wrap';
    quick.appendChild(this.btn('starve', 'hunger 100, wallet 0 → crisis path (loan → welfare → anger)', () => {
      this.call(r?.debugSetAgentStat({ versonaId: id, field: 'hunger', value: 100n }), 'starving…');
      this.call(r?.debugSetAgentStat({ versonaId: id, field: 'wallet', value: 0n }), 'and broke');
    }));
    quick.appendChild(this.btn('exhaust', 'needRest 100 → downs tools, heads home', () => this.call(r?.debugSetAgentStat({ versonaId: id, field: 'needRest', value: 100n }), 'exhausted')));
    quick.appendChild(this.btn('enrich +100ⓒ', 'wallet +100', () => this.call(r?.debugSetAgentStat({ versonaId: id, field: 'wallet', value: a.wallet + 100n }), 'enriched')));
    quick.appendChild(this.btn('cheer', 'joy 85', () => this.call(r?.debugElicitEmotion({ versonaId: id, etype: 'joy', intensity: 85, targetId: 0n, causeText: 'an admin\'s blessing' }), 'cheered')));
    quick.appendChild(this.btn('say…', 'speak as this NPC', () => {
      const text = prompt('Say as this NPC:');
      if (text?.trim()) this.call(r?.versonaSay({ versonaId: id, text: text.trim() }), 'said');
    }));
    quick.appendChild(this.btn('force birth here', 'stage a birth in this settlement', () => this.call(r?.debugForceBirth({ settlementId: a.settlementId }), 'a child is born')));
    b.appendChild(quick);
  }

  dispose(): void {
    this.releaseSubs();
    if (this.timer !== null) window.clearInterval(this.timer);
    this.win?.root.remove();
  }
}
